<script setup lang="ts">
import {computed, ref, watch} from "vue";
import Animations from "@/helpers/animations";
import UtilsService from "@/services/others/UtilsService";

interface Props {
	ranges: { min: number, max: number };
	currency: "RUB" | "USD" | "USDT" | "KZT";
	isError?: boolean;
	isLoading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	isError: false,
	isLoading: false,
});

const rootEl = ref<HTMLDivElement | null>(null);

const text = computed(() => {
	if (!props.ranges.min || !props.ranges.max) return "";

	return `${currencyFormatter.value.format(props.ranges.min)} - ${currencyFormatter.value.format(props.ranges.max)}`;
});

const utilsService = UtilsService.of();

const currencyFormatter = computed(() => utilsService.currencyFormatter(props.currency));

watch(() => props.isError, (value: boolean) => {
	if (value && rootEl.value) {
		Animations.shakeAnimation(rootEl.value);
	}
});
</script>

<template>
	<div ref="rootEl" class="input-amount-range">
		<Transition name="fade" mode="out-in">
			<div v-if="isLoading" class="input-amount-range__loader center"><span></span></div>
			<span v-else class="input-amount-range__text" :class="[{'is-error': isError}]">
				{{ text }}
			</span>
		</Transition>
	</div>
</template>

<style scoped lang="scss">
.input-amount-range {
	$root: #{&};
	@include text(12px, 400, var(--grey2));

	&__text.is-error {
		color: var(--red);
	}

	&__loader {
		position: relative;
		display: block;
		height: 14px;
		width: 14px;

		span {
			display: block;
			position: absolute;
			top: 0;
			left: 0;
			bottom: 0;
			right: 0;
			margin: auto;
			height: 14px;
			width: 14px;
			clip: rect(4px, 14px, 14px, 0);
			-webkit-animation: loader 0.7s linear infinite;
			animation: loader 0.7s linear infinite;

			&::after {
				content: "";
				display: block;
				position: absolute;
				top: 0;
				left: 0;
				bottom: 0;
				right: 0;
				margin: auto;
				height: 10px;
				width: 10px;
				border: 1px solid var(--grey2);
				border-radius: 50%;
			}
		}
	}

	@-webkit-keyframes loader {
		0% {
			-webkit-transform: rotate(0deg);
		}
		100% {
			-webkit-transform: rotate(360deg);
		}
	}

	@keyframes loader {
		0% {
			transform: rotate(0deg);
		}
		100% {
			transform: rotate(360deg);
		}
	}
}
</style>
